// ---------------------------------------------------------------------------
// SHADOW SCORING — what actually decided the winner.
//
// Morgan, Aug 22: "three columns for each candidate: continuous/raw ranking
// result, post-mutation ranking result, displayed score" and "whenever an
// earlier ceiling/cap is subsequently overridden by a floor/lift, show both
// rules, the score before each, and the final effect on ranking."
//
// Pure analysis over the `breakdown` scoring.ts already emits. It reads; it
// never scores. Nothing here can change what a user sees.
//
// The three columns are the point. Today `postMutationRanking` and `displayed`
// are the SAME NUMBER, because every floor and ceiling was applied to
// `pleasant`, which is both the ranking variable and the badge. That identity
// is the bug — printing them as separate columns is how it stops hiding.
// ---------------------------------------------------------------------------

import { NONLINEAR_RULES, type NonlinearRule, type RuleIntent } from "./scoreTerms";

const BY_LABEL = new Map<string, NonlinearRule>(NONLINEAR_RULES.map((r) => [r.label, r]));

export interface Breakdown {
  base: number;
  baseParts: { scenic: number; calm: number; community: number; blendedWithProfMatch: boolean };
  terms: Array<{
    label: string; delta: number; applied: boolean;
    before?: number; after?: number; violated?: boolean;
  }>;
  final: number;
  unexplained: number;
}

export interface MutationStep {
  label: string;
  kind: "floor" | "ceiling" | "envelope";
  intent: RuleIntent;
  before: number;
  after: number;
  delta: number;
  /** A ceiling that pushed the score DOWN, or a floor that pushed it UP.
   *  A rule that fired but moved nothing is not interesting. */
  bound: boolean;
  /** A ceiling that raised, or a floor that lowered. Should be impossible. */
  violated: boolean;
}

export interface Contradiction {
  ceiling: string;
  ceilingIntent: RuleIntent;
  /** What the ceiling held the route down to. */
  ceilingValue: number;
  floor: string;
  floorIntent: RuleIntent;
  /** What the floor lifted it back to. */
  floorValue: number;
  /** How many points of the ceiling's decision were undone. */
  undone: number;
  /** True when a rule introduced for the badge overrode a ranking decision. */
  displayOverrodeRanking: boolean;
}

export interface CandidateShadow {
  /** COUNTERFACTUAL: what the continuous model alone would score, with every
   *  floor/ceiling/lift removed. It is not a value that existed at runtime and
   *  is never used to decide whether a clamp bound. */
  rawRanking: number;
  /** Score after every nonlinear mutation. This is what ranking uses today. */
  postMutationRanking: number;
  /** What the badge shows. Identical to postMutationRanking today — which is
   *  precisely what we are trying to find out whether it should be. */
  displayed: number;
  mutations: MutationStep[];
  contradictions: Contradiction[];
  unexplained: number;
  /** Any mutation whose measured before/after broke its own invariant. */
  violations: string[];
}

/** Replay the breakdown in order, reconstructing the score before and after
 *  every nonlinear rule. */
export function analyseCandidate(b: Breakdown): CandidateShadow {
  const applied = b.terms.filter((t) => t.applied);
  let rawRanking = b.base;
  const mutations: MutationStep[] = [];

  for (const t of applied) {
    const rule = BY_LABEL.get(t.label);
    if (rule) {
      // MEASURED either side of the real production statement — never derived
      // from a running total. Morgan, Aug 22: "No reconstructed 'raw' score
      // should be used to determine whether a clamp bound."
      const before = t.before ?? NaN;
      const after = t.after ?? NaN;
      mutations.push({
        label: t.label, kind: rule.kind, intent: rule.intent,
        before, after, delta: after - before,
        bound: Math.abs(after - before) > 1e-9,
        violated: !!t.violated,
      });
    } else {
      // Continuous term. `rawRanking` is an explicit COUNTERFACTUAL — the score
      // the continuous model alone would give — and is never used to decide
      // whether a clamp bound.
      rawRanking += t.delta;
    }
  }

  // A ceiling is contradicted when a LATER floor lifts the score back above the
  // value that ceiling held it to. Order is the whole bug, so walk in order.
  const contradictions: Contradiction[] = [];
  for (let i = 0; i < mutations.length; i++) {
    const c = mutations[i];
    if (c.kind !== "ceiling" || !c.bound) continue;
    for (let j = i + 1; j < mutations.length; j++) {
      const f = mutations[j];
      if (f.kind !== "floor" || !f.bound) continue;
      if (f.after <= c.after) continue;
      contradictions.push({
        ceiling: c.label, ceilingIntent: c.intent, ceilingValue: c.after,
        floor: f.label, floorIntent: f.intent, floorValue: f.after,
        undone: +(f.after - c.after).toFixed(1),
        displayOverrodeRanking: f.intent === "display" && c.intent === "ranking",
      });
    }
  }

  return {
    violations: mutations.filter((m) => m.violated).map((m) => m.label),
    rawRanking: +rawRanking.toFixed(1),
    postMutationRanking: +b.final.toFixed(1),
    displayed: Math.round(b.final),
    mutations,
    contradictions,
    unexplained: +b.unexplained.toFixed(1),
  };
}

// ---------------------------------------------------------------------------
// The four counts Morgan asked for, accumulated across real searches.
// ---------------------------------------------------------------------------

export interface ShadowCounters {
  searches: number;
  candidates: number;
  /** componentFloor lifted a route above the avoidHighways ceiling. */
  floorOverAvoidHighways: number;
  /** componentFloor lifted a route above the named-arterial ceiling. */
  floorOverNamedArterial: number;
  /** epicHighwayLift cleared either ceiling. */
  epicOverCeiling: number;
  /** Any contradiction at all. */
  anyContradiction: number;
  /** The one that matters: the post-mutation winner differs from the winner
   *  the continuous model alone would have picked. Everything else is a
   *  displayed number moving. */
  winnerChangedByMutations: number;
  /** Winner changed AND the responsible mutation was display-motivated. */
  winnerChangedByDisplayRule: number;
  /** THE ONE THE FIRST RUN EXPOSED. Two candidates whose continuous scores
   *  differ materially but whose post-mutation scores are IDENTICAL. The floor
   *  did not flip a winner — it destroyed the basis for choosing one, and
   *  whatever runs next (duration, order) decides instead. Observed 22 Aug:
   *  raw 5.0 and raw -58.1 both landing on exactly 23.0. */
  discriminationCollapsed: number;
  /** Largest raw spread that collapsed to a single post-mutation value. */
  worstCollapseSpread: number;
}

export function emptyCounters(): ShadowCounters {
  return {
    searches: 0, candidates: 0,
    floorOverAvoidHighways: 0, floorOverNamedArterial: 0, epicOverCeiling: 0,
    anyContradiction: 0, winnerChangedByMutations: 0, winnerChangedByDisplayRule: 0,
    discriminationCollapsed: 0, worstCollapseSpread: 0,
  };
}

/** Fold one search into the counters. `pool` must be in the order scored;
 *  `idOf` names a candidate for the winner comparison (road names are ideal). */
export function foldSearch(
  counters: ShadowCounters,
  pool: Array<{ id: string; shadow: CandidateShadow }>,
): { rawWinner: string | null; postWinner: string | null; changed: boolean } {
  counters.searches += 1;
  counters.candidates += pool.length;

  for (const { shadow } of pool) {
    let hit = false;
    for (const c of shadow.contradictions) {
      hit = true;
      if (c.floor === "componentFloor" && c.ceiling === "avoidHighwaysCeiling") counters.floorOverAvoidHighways += 1;
      if (c.floor === "componentFloor" && c.ceiling === "namedArterialCeiling") counters.floorOverNamedArterial += 1;
      if (c.floor === "epicHighwayLift") counters.epicOverCeiling += 1;
    }
    if (hit) counters.anyContradiction += 1;
  }

  const best = (key: "rawRanking" | "postMutationRanking") =>
    pool.length
      ? pool.reduce((a, b) => (b.shadow[key] > a.shadow[key] ? b : a)).id
      : null;

  // Did any two candidates arrive at the same post-mutation score from
  // meaningfully different raw scores? That is the floor flattening the field.
  const byPost = new Map<number, number[]>();
  for (const { shadow } of pool) {
    const k = +shadow.postMutationRanking.toFixed(1);
    byPost.set(k, [...(byPost.get(k) ?? []), shadow.rawRanking]);
  }
  for (const raws of byPost.values()) {
    if (raws.length < 2) continue;
    const spread = Math.max(...raws) - Math.min(...raws);
    if (spread > 1) {
      counters.discriminationCollapsed += 1;
      counters.worstCollapseSpread = Math.max(counters.worstCollapseSpread, +spread.toFixed(1));
    }
  }

  const rawWinner = best("rawRanking");
  const postWinner = best("postMutationRanking");
  const changed = !!rawWinner && !!postWinner && rawWinner !== postWinner;
  if (changed) {
    counters.winnerChangedByMutations += 1;
    // Was a display-motivated rule implicated in the route that won?
    const winner = pool.find((p) => p.id === postWinner);
    if (winner?.shadow.contradictions.some((c) => c.displayOverrodeRanking))
      counters.winnerChangedByDisplayRule += 1;
  }
  return { rawWinner, postWinner, changed };
}

const sign = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(1)}`;

/** Three columns, then the mutation trail, then the contradictions. */
export function formatCandidate(id: string, s: CandidateShadow): string {
  const out: string[] = [];
  out.push(`  ${id}`);
  out.push(`    raw ranking ${s.rawRanking.toFixed(1).padStart(7)}   ` +
           `post-mutation ${s.postMutationRanking.toFixed(1).padStart(7)}   ` +
           `displayed ${String(s.displayed).padStart(4)}` +
           (Math.round(s.postMutationRanking) === s.displayed ? "   (ranking and display are the SAME number)" : ""));
  if (s.mutations.length) {
    out.push(`    mutations, in execution order:`);
    for (const m of s.mutations)
      out.push(`      ${m.bound ? "BOUND  " : "no-op  "}${m.label.padEnd(24)} ` +
        `${m.before.toFixed(1).padStart(7)} → ${m.after.toFixed(1).padStart(7)}  ` +
        `${sign(m.delta).padStart(8)}   [${m.kind}, intent: ${m.intent}]`);
  }
  for (const c of s.contradictions) {
    out.push(`    ⚠ CONTRADICTION: ${c.ceiling} held this to ${c.ceilingValue.toFixed(1)} ` +
             `[${c.ceilingIntent}], then ${c.floor} lifted it to ${c.floorValue.toFixed(1)} ` +
             `[${c.floorIntent}] — ${c.undone} points of that decision undone.` +
             (c.displayOverrodeRanking ? `\n      A DISPLAY rule overrode a RANKING rule.` : ""));
  }
  for (const v of s.violations)
    out.push(`    ✗ INVARIANT VIOLATED by ${v} — a ceiling raised, or a floor lowered.`);
  if (Math.abs(s.unexplained) > 0.05)
    out.push(`    UNEXPLAINED ${sign(s.unexplained)} — decomposition incomplete, do not trust the columns above.`);
  return out.join("\n");
}

export function formatCounters(c: ShadowCounters): string {
  const pct = (n: number) => c.candidates ? `${((100 * n) / c.candidates).toFixed(1)}%` : "—";
  return [
    ``,
    `SHADOW COUNTS  ${c.searches} searches, ${c.candidates} candidates`,
    `  componentFloor lifted above avoidHighwaysCeiling   ${String(c.floorOverAvoidHighways).padStart(5)}  ${pct(c.floorOverAvoidHighways)}`,
    `  componentFloor lifted above namedArterialCeiling   ${String(c.floorOverNamedArterial).padStart(5)}  ${pct(c.floorOverNamedArterial)}`,
    `  epicHighwayLift cleared a ceiling                  ${String(c.epicOverCeiling).padStart(5)}  ${pct(c.epicOverCeiling)}`,
    `  any contradiction                                  ${String(c.anyContradiction).padStart(5)}  ${pct(c.anyContradiction)}`,
    ``,
    `  WINNER CHANGED by post-score mutations             ${String(c.winnerChangedByMutations).padStart(5)}  of ${c.searches} searches`,
    `    ...of which a DISPLAY rule was implicated        ${String(c.winnerChangedByDisplayRule).padStart(5)}`,
    ``,
    `  DISCRIMINATION COLLAPSED (>=2 candidates, different raw, identical post)`,
    `    occurrences ${String(c.discriminationCollapsed).padStart(5)}   worst raw spread flattened: ${c.worstCollapseSpread.toFixed(1)} points`,
    ``,
    `  A collapse is not a flipped winner — it is worse. The score stops`,
    `  distinguishing the routes at all, and something downstream decides.`,
    ``,
    `  A contradiction that only moves a displayed number is untidy.`,
    `  One that moves the winner is the product being wrong.`,
    ``,
  ].join("\n");
}
